import { useEffect, useState } from "react";
import { AdminToolCorrectiveProgramResult } from "../../models/interfaces/corrective-exercises/admin-tool-corrective-program-result";
import { AdminToolArticleGroupsResult } from "../../models/interfaces/articles/admin-tool-article-groups-result";
import CorrectiveProgramsService from "../../utilities/services/corrective-programs-service/corrective-programs-serivce";
import ArticleApiService from "../../utilities/services/article-service/article-api-service";
import ArticleUtils from "../../utilities/article-utils";
import Article from "../../models/interfaces/articles/article";
import { ToggleButton } from "../../components/forms/toggle-button";
import { Button } from "../../components/button/button";

type ContentDay = {
    day: number;
    correctiveTitle: string;
    primaryArticleTitle?: string;
    pseudoArticleTitle?: string;
};

const COMPONENT_CLASS = "c-view-wellness-program-content-tool";

const ViewWellnessProgramContentTool: React.FC = () => {
    const [programs, setPrograms] = useState<AdminToolCorrectiveProgramResult[]>([]);
    const [primaryArticleGroups, setPrimaryArticleGroups] = useState<AdminToolArticleGroupsResult[]>([]);
    const [pseudoArticleGroups, setPseudoArticleGroups] = useState<AdminToolArticleGroupsResult[]>([]);
    const [hasPain, setHasPain] = useState(false);
    const [content, setContent] = useState<ContentDay[]>([]);

    const [selectedProgram, setSelectedProgram] =
        useState<AdminToolCorrectiveProgramResult>();

    const [selectedPrimaryArticleGroup, setSelectedPrimaryArticleGroup] =
        useState<AdminToolArticleGroupsResult>();

    const [selectedPseudoArticleGroup, setSelectedPseudoArticleGroup] =
        useState<AdminToolArticleGroupsResult>();

    const filteredPrograms = programs.filter(p => {
        const isPainProgram = p.programId.toUpperCase().includes("PAIN");
        return hasPain ? isPainProgram : !isPainProgram;
    });

    const filteredPseudoArticleGroups = hasPain
        ? pseudoArticleGroups.filter(g =>
            !g.groupId.toUpperCase().includes("BREATHING")
        )
        : pseudoArticleGroups;


    useEffect(() => {
        if (!selectedProgram) return;

        const isPainProgram =
            selectedProgram.programId.toUpperCase().includes("PAIN");

        if (hasPain !== isPainProgram) {
            setSelectedProgram(undefined);
        }
    }, [hasPain, selectedProgram]);

    useEffect(() => {
        if (
            hasPain &&
            selectedPseudoArticleGroup &&
            selectedPseudoArticleGroup.groupId.toUpperCase().includes("BREATHING")
        ) {
            setSelectedPseudoArticleGroup(undefined);
        }
    }, [hasPain, selectedPseudoArticleGroup]);

    useEffect(() => {
        const load = async () => {
            const [
                programs,
                primaryGroups,
                pseudoGroups,
            ] = await Promise.all([
                CorrectiveProgramsService.getAdminToolCorrectivePrograms(),
                ArticleApiService.getAdminToolPrimaryArticleGroups(),
                ArticleApiService.getPseudoArticleGroups(),
            ]);

            setPrograms(programs);
            setPrimaryArticleGroups(primaryGroups);
            setPseudoArticleGroups(pseudoGroups);
        };

        load();
    }, []);

    const calculate = async () => {
        if (!selectedProgram || !selectedPrimaryArticleGroup || !selectedPseudoArticleGroup) {
            alert("Please select all required fields");
            return;
        }

        const primaryArticles: Article[] =
            await ArticleUtils.adminEmulateUserPrimaryMskArticles(
                selectedPrimaryArticleGroup.groupId
            );

        const pseudoArticles: Article[] =
            await ArticleUtils.adminEmulateUserPseudoMskArticles(
                selectedPseudoArticleGroup.groupId,
                hasPain,
                selectedProgram.groupId
            );

        let primaryIndex = 0;
        let pseudoIndex = 0;

        const result: ContentDay[] = [];

        for (let day = 1; day <= selectedProgram.programDays; day++) {
            const routine =
                selectedProgram.routineTitlesByDay.find(d => d.day === day);

            const row: ContentDay = {
                day,
                correctiveTitle: routine?.title ?? `Day ${day} Routine`,
            };

            if (day % 2 === 1 && primaryIndex < primaryArticles.length) {
                row.primaryArticleTitle =
                    primaryArticles[primaryIndex].translations?.en.title;
                primaryIndex++;
            }

            if (day % 2 === 0 && pseudoIndex < pseudoArticles.length) {
                row.pseudoArticleTitle =
                    pseudoArticles[pseudoIndex].translations?.en.title;
                pseudoIndex++;
            }

            result.push(row);
        }

        setContent(result);
    };

    return (
        <div className={COMPONENT_CLASS}>

            {/* Program */}
            <select
                key={hasPain ? "pain" : "no-pain"}
                value={selectedProgram?.programId ?? ""}
                onChange={(e) =>
                    setSelectedProgram(
                        filteredPrograms.find(p => p.programId === e.target.value)
                    )
                }
            >
                <option value="">Select program</option>
                {filteredPrograms.map(p => (
                    <option key={p.programId} value={p.programId}>
                        {p.programId}
                    </option>
                ))}
            </select>

            {/* Primary */}
            <select
                value={selectedPrimaryArticleGroup?.groupId ?? ""}
                onChange={(e) =>
                    setSelectedPrimaryArticleGroup(
                        primaryArticleGroups.find(g => g.groupId === e.target.value)
                    )
                }
            >
                <option value="">Primary article group</option>
                {primaryArticleGroups.map(g => (
                    <option key={g.groupId} value={g.groupId}>
                        {g.groupId}
                    </option>
                ))}
            </select>

            {/* Pseudo */}
            <select
                value={selectedPseudoArticleGroup?.groupId ?? ""}
                onChange={(e) =>
                    setSelectedPseudoArticleGroup(
                        filteredPseudoArticleGroups.find(
                            g => g.groupId === e.target.value
                        )
                    )
                }
            >
                <option value="">Pseudo article group</option>
                {filteredPseudoArticleGroups.map(g => (
                    <option key={g.groupId} value={g.groupId}>
                        {g.groupId}
                    </option>
                ))}
            </select>

            <ToggleButton
                id="hasPain"
                mainLabelText="Has Pain"
                isChecked={hasPain}
                onClick={() => setHasPain(p => !p)}
            />

            <Button buttonText="Calculate" onClick={calculate} />

            {content.length > 0 && (
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "1rem",
                        marginTop: "2rem",
                    }}
                >
                    {content.map(d => (
                        <div
                            key={d.day}
                            style={{
                                width: "180px",
                                height: "180px",
                                border: "1px solid black",
                                padding: "0.75rem",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "flex-start",
                                fontSize: "0.85rem",
                                backgroundColor: "#fff",
                            }}
                        >
                            <div
                                style={{
                                    fontWeight: 600,
                                    marginBottom: "0.5rem",
                                }}
                            >
                                Day {d.day}
                            </div>

                            <div
                                style={{
                                    marginBottom: "0.25rem",
                                    lineHeight: 1.2,
                                    wordBreak: "break-word",
                                }}
                            >
                                C: {d.correctiveTitle}
                            </div>

                            {d.primaryArticleTitle && (
                                <div
                                    style={{
                                        marginBottom: "0.25rem",
                                        lineHeight: 1.2,
                                        wordBreak: "break-word",
                                    }}
                                >
                                    FA: {d.primaryArticleTitle}
                                </div>
                            )}

                            {d.pseudoArticleTitle && (
                                <div
                                    style={{
                                        marginBottom: "0.25rem",
                                        lineHeight: 1.2,
                                        wordBreak: "break-word",
                                    }}
                                >
                                    PFA: {d.pseudoArticleTitle}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ViewWellnessProgramContentTool;
